import React, { useCallback, useState } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import SettingsRemoteIcon from '@mui/icons-material/SettingsRemote';
import { SpreadsheetDocumentsData } from '../../models/SpreadsheetDocument';
import {
  NvrTemplateSpreadsheetService,
  DEFAULT_NVR_TEMPLATE_COLUMNS,
} from '../../services/NvrTemplateSpreadsheetService';
import {
  exportNvrTemplateXls,
  exportNvrTemplatesZip,
  isFocusAiDocument,
  pickNvrColumns,
} from '../../services/nvrTemplateExport';
import { SpreadsheetDocument } from '../../models/SpreadsheetDocument';
import NvrRemoteRegisterDialog from './NvrRemoteRegisterDialog';
import SpreadsheetDocuments from '../../components/spreadsheet-docs/SpreadsheetDocuments';
import { getUserInfo } from '../../utils/auth';

interface NvrTemplateSpreadsheetProps {
  projectId: number;
}

/**
 * NVR 원격등록 템플릿 — 구역별 다중 문서.
 *
 * 문서 1개 = 구역 1개 = NVR 에 올리는 .xls 1개. 편집은 IP / CH NAME 두 열만 하고,
 * 내보내기 버튼이 고정값(IPC/CH NUM/80/admin/"1234"/RTSP 빈 셀)을 채운 NVR 형식 .xls 를
 * 만든다. 기존 템플릿 .xls 를 업로드하면 IP / CH NAME 열만 추출해 문서가 된다.
 * 포커스AI [CSV 등록] 형식으로 올린 문서는 10열을 그대로 두고, 녹화기 원격 등록을 할 수 있다.
 */
const NvrTemplateSpreadsheet: React.FC<NvrTemplateSpreadsheetProps> = ({ projectId }) => {
  const role = getUserInfo()?.role;
  const writable = role === 'admin' || role === 'editor';

  const load = useCallback(() => NvrTemplateSpreadsheetService.get(projectId), [projectId]);
  const save = useCallback(
    (data: SpreadsheetDocumentsData) => NvrTemplateSpreadsheetService.update(projectId, data),
    [projectId],
  );

  const [registerDoc, setRegisterDoc] = useState<SpreadsheetDocument | null>(null);
  // 포커스AI CSV 형식으로 올린 문서만 녹화기 원격 등록 버튼을 단다(기존 NVR .xls 형식은 대상 아님).
  const renderDocActions = useCallback(
    (doc: SpreadsheetDocument) =>
      writable && isFocusAiDocument(doc) ? (
        <Tooltip title="녹화기에 원격 등록 (포커스AI)">
          <IconButton
            size="small"
            onClick={() => setRegisterDoc(doc)}
            aria-label={`${doc.name} NVR 원격 등록`}
          >
            <SettingsRemoteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null,
    [writable],
  );

  return (
    <>
      <SpreadsheetDocuments
        key={projectId}
        load={load}
        save={save}
        writable={writable}
        title="NVR 원격등록 템플릿"
        description="💡 문서 1개가 구역(NVR) 1개입니다. 포커스AI 녹화기는 [CSV 등록] 형식(protocol,ip,…,title)의 .csv 를 올리면 10열 그대로 문서가 되고, 문서 줄의 원격 등록 버튼으로 통합서버를 거쳐 녹화기에 바로 등록합니다(다운로드도 같은 .csv). 기존 NVR 은 IP 와 CH NAME 만 입력하면 되고, 나머지 열(IPC / CH NUM / 80 / admin / 1234)은 내보낼 때 자동으로 채워집니다. 다운로드 버튼은 NVR 이 바로 읽는 .xls 를 만듭니다(Excel 로 다시 저장하지 마세요 — PASSWORD 가 숫자로 바뀌어 등록되지 않습니다). 전체 다운로드는 문서별 .xls 파일을 ZIP으로 묶습니다. 기존 템플릿을 업로드하면 IP / CH NAME 만 가져옵니다. 폴더 업로드는 하위 폴더의 .xls / .xlsx 파일까지 파일별 문서로 추가하며, 파일명과 하위 경로로 구분합니다."
        defaultColumns={DEFAULT_NVR_TEMPLATE_COLUMNS}
        newDocName="새 구역"
        importGrid={pickNvrColumns}
        exportDoc={exportNvrTemplateXls}
        exportAllDocs={exportNvrTemplatesZip}
        allowFolderUpload
        renderDocActions={renderDocActions}
      />
      <NvrRemoteRegisterDialog
        projectId={projectId}
        doc={registerDoc}
        onClose={() => setRegisterDoc(null)}
      />
    </>
  );
};

export default NvrTemplateSpreadsheet;
